import { CartAddEvent, CartErrorEvent } from '@theme/events';

class CroBuyBox extends HTMLElement {
  #form = null;
  #variantIdInput = null;
  #sellingPlanInput = null;
  #subVariantInput = null;
  #submitButtons = [];
  #errorEl = null;
  #planRadios = [];
  #variantRadios = [];
  #lastClickedSubmitter = null;
  #addonChecks = [];
  #dueValue = null;
  #atcTotal = null;

  connectedCallback() {
    this.#form = this.querySelector('form.cro-buy-box__form');
    if (!this.#form) return;

    this.#variantIdInput = this.#form.querySelector('input[name="id"]');
    this.#sellingPlanInput = this.#form.querySelector('input[name="selling_plan"]');
    this.#subVariantInput = this.#form.querySelector('input[name="cro-sub-variant"]');
    this.#submitButtons = [...this.#form.elements].filter((el) => el.matches('[data-cro-atc], [data-cro-sticky-atc], .cro-buy-box__atc'));
    this.#errorEl = this.#form.querySelector('[data-cro-error], .cro-buy-box__error');
    this.#planRadios = this.#form.querySelectorAll('input[name="cro-plan"]');
    this.#addonChecks = [...this.#form.querySelectorAll('.cro-addon__check')];
    this.#dueValue = this.#form.querySelector('[data-cro-due-value], .cro-buy-box__due-value');
    this.#atcTotal = this.#form.querySelector('[data-cro-atc-total], .cro-buy-box__atc-total');

    this.#variantRadios = this.#form.querySelectorAll('input[name="cro-variant"]');

    this.#planRadios.forEach((radio) => radio.addEventListener('change', this.#onPlanChange));
    this.#variantRadios.forEach((radio) => radio.addEventListener('change', this.#onVariantChange));
    this.#form.addEventListener('change', this.#onAddonChange);
    this.#watchBazaarvoice();
    this.#scope.querySelector('[data-cro-rating-count], .cro-buy-box__rating-count')?.addEventListener('click', this.#scrollToReviews);
    this.#form.addEventListener('click', this.#onFormClick);
    this.#form.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#planRadios.forEach((radio) => radio.removeEventListener('change', this.#onPlanChange));
    this.#variantRadios.forEach((radio) => radio.removeEventListener('change', this.#onVariantChange));
    this.#form?.removeEventListener('click', this.#onFormClick);
    this.#form?.removeEventListener('change', this.#onAddonChange);
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  get #scope() {
    return this.closest('.cro-pdp-hero') ?? this;
  }

  #scrollToReviews = (event) => {
    const reviews = document.querySelector('[data-bv-show="reviews"]');
    if (!reviews) return;
    event.preventDefault();
    reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  #watchBazaarvoice() {
    const source = this.#scope.querySelector('[data-cro-bv-source], .cro-buy-box__bv-source');
    if (!source) return;
    const apply = () => {
      const rating = parseFloat(source.querySelector('.bv_avgRating_component_container')?.textContent ?? '');
      const count = parseInt((source.querySelector('.bv_numReviews_text')?.textContent ?? '').replace(/\D/g, ''), 10);
      if (!Number.isFinite(rating) || !Number.isFinite(count) || count < 1) return false;
      const row = this.#scope.querySelector('[data-bv-rating-row]');
      const value = row?.querySelector('[data-cro-rating-value], .cro-buy-box__rating-value');
      const stars = row?.querySelector('[data-cro-stars], .cro-buy-box__stars');
      if (value) value.textContent = rating.toFixed(1);
      if (stars) stars.textContent = '★'.repeat(Math.round(rating)) + '☆'.repeat(5 - Math.round(rating));
      row?.querySelector('[data-cro-rating-count], .cro-buy-box__rating-count')?.classList.remove('hidden');
      this.#scope.querySelectorAll('[data-bv-count]').forEach((el) => (el.textContent = String(count)));
      return true;
    };
    if (apply()) return;
    const observer = new MutationObserver(() => {
      if (apply()) observer.disconnect();
    });
    observer.observe(source, { childList: true, subtree: true, characterData: true });
  }

  #onAddonChange = (event) => {
    const target = event.target;
    if (target.matches('.cro-addon__variant')) {
      const card = target.closest('.cro-addon');
      const option = target.selectedOptions[0];
      const check = card?.querySelector('.cro-addon__check');
      const price = card?.querySelector('.cro-addon__price');
      if (check && option) {
        check.dataset.variantId = option.value;
        check.dataset.priceCents = option.dataset.priceCents || '0';
      }
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
    } else if (!target.matches('.cro-addon__check')) {
      return;
    }
    this.#updateTotal();
  };

  #selectedAddons() {
    return this.#addonChecks.filter((check) => check.checked && check.dataset.variantId);
  }

  #updateTotal() {
    if (!this.#dueValue) return;
    const base = parseInt(this.#dueValue.dataset.baseCents || '0', 10);
    const total = this.#selectedAddons().reduce((sum, check) => sum + parseInt(check.dataset.priceCents || '0', 10), base);
    const format = this.#dueValue.dataset.moneyFormat || '${{amount}}';
    const formatted = format.replace(/\{\{\s*amount\s*\}\}/, (total / 100).toFixed(2));
    this.#dueValue.textContent = formatted;
    if (this.#atcTotal) this.#atcTotal.textContent = formatted;
  }

  #onPlanChange = (event) => {
    const radio = (event.target);
    if (!radio.checked) return;

    const variantId = radio.dataset.variantId;
    const sellingPlanId = radio.dataset.sellingPlanId;

    this.#applyPlan(variantId, sellingPlanId);
  };

  #onVariantChange = (event) => {
    const radio = event.target;
    if (!radio.checked) return;
    if (this.#variantIdInput) this.#variantIdInput.value = radio.value;
    this.#form.querySelectorAll('[data-cro-variant-price], [data-cro-atc-total]').forEach((el) => (el.textContent = radio.dataset.price));
  };

  #applyPlan(variantId, sellingPlanId) {
    const target = this.#subVariantInput ?? this.#variantIdInput;
    if (variantId && target) target.value = variantId;
    if (this.#sellingPlanInput) this.#sellingPlanInput.value = sellingPlanId || '';
  }

  #onFormClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('button[type="submit"]') : null;
    if (target instanceof HTMLButtonElement) this.#lastClickedSubmitter = target;
  };

  #onSubmit = async (event) => {
    event.preventDefault();
    if (!this.#form || !this.#variantIdInput) return;

    const submitter =
      event instanceof SubmitEvent && event.submitter instanceof HTMLButtonElement
        ? event.submitter
        : this.#lastClickedSubmitter;
    if (submitter && submitter.dataset.variantId) this.#applyPlan(submitter.dataset.variantId, submitter.dataset.sellingPlanId);

    const id = this.#variantIdInput.value;
    const sellingPlanRaw = this.#sellingPlanInput?.value?.trim();
    const skipUpsell = this.#form.querySelector('input[name="cro-plan"]:checked')?.hasAttribute('data-skip-upsell');
    const subVariantId = this.#subVariantInput?.value?.trim();

    this.#setLoading(true);
    this.#clearError();

    const items = [{ id, quantity: 1 }];
    if (subVariantId && sellingPlanRaw) {
      items.push({ id: subVariantId, quantity: 1, selling_plan: parseInt(sellingPlanRaw, 10) });
    } else if (sellingPlanRaw) {
      items[0].selling_plan = parseInt(sellingPlanRaw, 10);
    }
    items.push(...this.#selectedAddons().map((check) => ({ id: check.dataset.variantId, quantity: 1 })));

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const body = {
      items,
      sections: sectionIds.join(','),
    };

    try {
      const response = await fetch(`${Theme.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok && response.status !== 422) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json().catch(() => {
        throw new Error('Invalid JSON response from server');
      });

      if (data.status) {
        const message = data.message || data.description || 'Could not add this item to your cart.';
        this.#showError(message);
        window.dispatchEvent(new CartErrorEvent(this.id, message));

        (submitter ?? this).dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'cro-buy-box',
            itemCount: items.length,
            productId: this.dataset.productId,
          })
        );
        return;
      }

      await (typeof scheduler !== 'undefined' && scheduler.yield ? scheduler.yield() : new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0))));

      (submitter ?? this).dispatchEvent(
        new CartAddEvent(data, id.toString(), {
          source: 'cro-buy-box',
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );

      if (sellingPlanRaw || skipUpsell) return;

      document.dispatchEvent(
        new CustomEvent('cart:added', {
          detail: {
            productId: this.dataset.productId,
            productHandle: this.dataset.productHandle || window.location.pathname.split('/').pop(),
            variantId: id.toString(),
            quantity: 1,
            items: data.items,
            isUpsell: false,
          },
        })
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add item to cart. Please try again.';
      this.#showError(message);
      window.dispatchEvent(new CartErrorEvent(this.id, message));
    } finally {
      this.#setLoading(false);
    }
  };

  #setLoading(isLoading) {
    this.#submitButtons.forEach((button) => {
      button.toggleAttribute('disabled', isLoading);
      button.setAttribute('aria-busy', String(isLoading));
      button.style.opacity = isLoading ? '0.6' : '';
    });
  }

  #showError(message) {
    if (!this.#errorEl) return;
    this.#errorEl.textContent = message;
  }

  #clearError() {
    if (!this.#errorEl) return;
    this.#errorEl.textContent = '';
  }
}

if (!customElements.get('cro-buy-box')) {
  customElements.define('cro-buy-box', CroBuyBox);
}
